/**
 * The people who take the company's meetings, and whether they are free.
 *
 * UNGUARDED like the rest of `@repo/db/queries`; `@repo/core` asks first. Two
 * questions, asked from every place that claims a meeting hour:
 *
 *   • WHO hosts — {@link listMeetingHosts}: Admins carrying the meetings marker.
 *   • WHO IS FREE right now — {@link freeMeetingHosts}: the busy rule in SQL,
 *     for the transactions that claim an hour and cannot call `@repo/core`.
 *
 * Plus the lock every one of those transactions takes, so that a host saving
 * a diary entry and a stranger taking the same hour serialise instead of both
 * finding the host free. Who SITS IN a meeting is `./booking-hosts.ts`.
 */
import { and, asc, inArray, type SQL, sql } from 'drizzle-orm'

import type { Queryable, Tx } from '../client.ts'
import { err, ok, type Result } from '../result.ts'
import { user } from '../schema/auth.ts'
import { bookingHost } from '../schema/booking-hosts.ts'
import { booking } from '../schema/bookings.ts'
import { calendarEntry } from '../schema/calendar-entries.ts'
import { offering } from '../schema/offerings.ts'
import { orderItem } from '../schema/orders.ts'
import { type DbError, dbError } from './errors.ts'

/**
 * The marker token, and the main role it has to sit beside.
 *
 * Spelled out here rather than imported: `@repo/auth` sits ABOVE this package,
 * and its `MEETING_HOST_ROLE` / `MEETING_HOST_BASE_ROLE` are the source. A core
 * test pins the two pairs equal (`meeting-host.test.ts`), so they cannot drift
 * without a red build.
 */
export const MEETING_HOST_TOKEN = 'meetings'
export const MEETING_HOST_BASE_TOKEN = 'admin'

/** One host, as a notice or a dropdown needs them. */
export interface MeetingHost {
  readonly id: string
  readonly name: string | null
  readonly email: string
}

/**
 * "This `user` row hosts meetings" — the SQL twin of `isMeetingHost` in
 * `@repo/auth/access-control`, plus the two things only the database knows.
 *
 * Both tokens, for the reason given there. NOT banned: a closed account is
 * anonymised and banned (`closeUserAccount`), and a blocked one must not be put
 * in front of a customer. NOT invented by a training rehearsal: an invented
 * Admin on the real meetings rota would be a name a stranger is told to expect.
 *
 * `@>` over the split column, never `LIKE`, for the reason `holdsAnyRole` in
 * `./announcements.ts` gives.
 */
export function isMeetingHostRow(): SQL {
  return sql`(
    string_to_array(coalesce(${user.role}, ''), ',') @> ARRAY[${MEETING_HOST_TOKEN}, ${MEETING_HOST_BASE_TOKEN}]::text[]
    AND ${user.banned} = false
    AND ${user.trainingSessionId} IS NULL
  )`
}

/**
 * Everybody who takes meetings today, by name.
 *
 * `Queryable` so a claim can read it inside its own transaction. An EMPTY list
 * is a real answer with a rule attached: nobody holds the role yet, and the
 * meeting paths then behave exactly as they did before it existed.
 */
export async function listMeetingHosts(db: Queryable): Promise<Result<MeetingHost[], DbError>> {
  try {
    const rows = await db
      .select({ id: user.id, name: user.name, email: user.email })
      .from(user)
      .where(isMeetingHostRow())
      .orderBy(asc(user.name), asc(user.id))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to list meeting hosts', cause))
  }
}

/** A window being asked about, and the meeting being moved, if one is. */
export interface HostProbe {
  readonly startsAt: Date
  readonly endsAt: Date
  /** A meeting being MOVED must not be found standing in its own way. */
  readonly excludeBookingId?: string
}

/**
 * "Does the person `hostId` names hold any time in this window?" — THE busy
 * rule, as a SQL predicate.
 *
 * ── WHY A SECOND SPELLING OF THE RULE EXISTS ─────────────────────────────
 * `@repo/core`'s availability check is the source of truth, and every screen
 * goes through it. The claims cannot: a free call, a strategy checkout and a
 * reschedule each decide WHO is free and write them onto the meeting inside
 * one transaction, under the offering's lock, and `@repo/db` cannot call
 * `@repo/core`. A check run before the transaction would reopen the window the
 * lock closes. So this is the same rule inlined, as `declaresSpanCovering` in
 * `./capacity.ts` is for shoots — and it reads exactly what the timeline
 * readers in `./performer.ts` read, so the two cannot disagree about a person:
 *
 *   • a calendar entry of theirs overlapping the window;
 *   • a live reservation (anything but CANCELLED / NO_SHOW, PENDING included)
 *     whose BLOCKED window overlaps it, where they are the performer
 *     (`booking.performer_user_id`), the assignee of the line behind it
 *     (`order_item.assigned_user_id`) or one of its hosts (`booking_host`).
 *
 * No travel gap: meetings are back to back by design, which is the `gapMinutes:
 * 0` the core check is asked with for them. And no "declared hours" test —
 * whether the window sits inside the published opening hours is the caller's
 * check, made before it gets here.
 */
export function hostHoldsTime(hostId: SQL | typeof user.id, probe: HostProbe): SQL {
  const excluded = probe.excludeBookingId
    ? sql`AND ${booking.id} <> ${probe.excludeBookingId}`
    : sql``
  return sql`(
    EXISTS (
      SELECT 1 FROM ${calendarEntry}
       WHERE ${calendarEntry.userId} = ${hostId}
         AND ${calendarEntry.startsAt} < ${probe.endsAt}
         AND ${calendarEntry.endsAt} > ${probe.startsAt}
    )
    OR EXISTS (
      SELECT 1 FROM ${booking}
        LEFT JOIN ${orderItem} ON ${orderItem.id} = ${booking.orderItemId}
       WHERE ${booking.blockedFrom} < ${probe.endsAt}
         AND ${booking.blockedTo} > ${probe.startsAt}
         AND ${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')
         ${excluded}
         AND (
           ${booking.performerUserId} = ${hostId}
           OR ${orderItem.assignedUserId} = ${hostId}
           OR EXISTS (
             SELECT 1 FROM ${bookingHost}
              WHERE ${bookingHost.bookingId} = ${booking.id}
                AND ${bookingHost.userId} = ${hostId}
           )
         )
    )
  )`
}

/** Who could be put on a meeting in this window, and who of them is free. */
export interface HostAvailability {
  /** Every host considered — all of them, or the asked-about ones still hosting. */
  readonly hosts: readonly MeetingHost[]
  /** The ones holding no time in the window, in the same order. */
  readonly free: readonly MeetingHost[]
}

/**
 * Which hosts are free for the whole of this window — read where a claim can
 * trust it.
 *
 * Meant to run INSIDE the transaction that claims the hour, after
 * {@link lockOfferingCalendar}: that is what makes the answer still true at
 * COMMIT. Read outside a lock it is a hint, like any other read.
 *
 * `'ALL'` asks about every current host. A list narrows it — and is checked
 * against the role again, so a host who lost the marker between the page load
 * and the claim is not written onto the meeting.
 *
 * `hosts` empty means nobody takes meetings: the caller's rollout rule applies
 * (no host requirement at all). `hosts` non-empty with `free` empty means the
 * hour has gone.
 */
export async function freeMeetingHosts(
  db: Queryable,
  input: HostProbe & { readonly hostIds: readonly string[] | 'ALL' },
): Promise<Result<HostAvailability, DbError>> {
  if (input.hostIds !== 'ALL' && input.hostIds.length === 0) return ok({ hosts: [], free: [] })
  try {
    const rows = await db
      .select({
        id: user.id,
        name: user.name,
        email: user.email,
        busy: sql<boolean>`${hostHoldsTime(user.id, input)}`,
      })
      .from(user)
      .where(
        and(
          isMeetingHostRow(),
          input.hostIds === 'ALL' ? undefined : inArray(user.id, [...input.hostIds]),
        ),
      )
      .orderBy(asc(user.name), asc(user.id))

    const hosts = rows.map(({ id, name, email }) => ({ id, name, email }))
    const free = rows.filter((row) => !row.busy).map(({ id, name, email }) => ({ id, name, email }))
    return ok({ hosts, free })
  } catch (cause) {
    return err(dbError('Failed to read which meeting hosts are free', cause))
  }
}

/**
 * Take THE lock every path that claims an hour on this offering takes.
 *
 * `pg_advisory_xact_lock(hashtext(offering_id))`, written in this one place so
 * that every taker spells it the same way — `claimMeetingSlot` and
 * `moveMeetingSlot` (`./bookings.ts`) and `insertOrder` (`./orders.ts`) all call
 * it, because a lock only serialises what takes the SAME one. Held to the end
 * of the transaction and released by COMMIT or ROLLBACK; there is nothing to
 * unlock by hand.
 *
 * Throws on failure, like `deleteCartsForUser`: it only makes sense inside the
 * caller's transaction, and the caller's `try` is where that fails.
 */
export async function lockOfferingCalendar(tx: Tx, offeringId: string): Promise<void> {
  await tx.execute(sql`select pg_advisory_xact_lock(hashtext(${offeringId}))`)
}

/**
 * The same lock, found by the offering's slug — for writes that do not hold
 * the offering row, chiefly a host saving a calendar entry (pass `MEETING_SLUG`
 * from `@repo/core`). `hashtext` of the id read from the row is the same number
 * as `hashtext` of the id passed in, so this and {@link lockOfferingCalendar}
 * are one lock. An unknown slug locks nothing, which is the honest outcome:
 * there is no calendar to protect.
 */
export async function lockOfferingCalendarBySlug(tx: Tx, slug: string): Promise<void> {
  await tx.execute(
    sql`select pg_advisory_xact_lock(hashtext(${offering.id})) from ${offering} where ${offering.slug} = ${slug}`,
  )
}
